// client/src/components/campaign/CampaignDetail.jsx
//
// One marketing campaign: status control, KPI goal-vs-actual (ROI), and the
// content pipeline grouped by approval stage (On-Screen 2.0).

import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { FaArrowLeft, FaHome, FaRegCalendarAlt } from 'react-icons/fa';
import AppFooter from '../AppFooter';
import { getSessionLocal } from '../../services/sessionLocal';
import { can } from '../../services/rolePermissions';
import {
  getCampaign, getCampaignContent, getCampaignRoi, setCampaignStatus,
  CAMPAIGN_STATUSES,
} from '../../services/campaignsApi';
import { APPROVAL_LABELS } from '../../services/contentApprovalApi';
import CampaignForm from './CampaignForm';
import GeneratePlanModal from './GeneratePlanModal';
import CampaignEmailPanel from './CampaignEmailPanel';
import AttributionPanel from './AttributionPanel';

const STAGE_ORDER = ['idea', 'draft', 'in_review', 'changes_requested', 'approved', 'rejected'];

export default function CampaignDetail({ tenantId: tenantProp }) {
  const params = useParams();
  const tenantId = tenantProp || params.tenant;
  const campaignId = params.campaignId;
  const navigate = useNavigate();
  const { role } = getSessionLocal();
  const canEdit = can(role, 'events', 'update');

  const [campaign, setCampaign] = useState(null);
  const [content, setContent] = useState({ byStage: {}, total: 0 });
  const [roi, setRoi] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [editing, setEditing] = useState(false);
  const [planning, setPlanning] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [c, ct, r] = await Promise.all([
        getCampaign(tenantId, campaignId),
        getCampaignContent(tenantId, campaignId).catch(() => ({ byStage: {}, total: 0 })),
        getCampaignRoi(tenantId, campaignId).catch(() => ({ data: null })),
      ]);
      setCampaign(c.data);
      setContent({ byStage: ct.byStage || {}, total: ct.total || 0, data: ct.data || [] });
      setRoi(r.data || null);
    } catch (err) {
      setError(err?.response?.data?.message || err.message || 'Failed to load campaign');
    } finally {
      setLoading(false);
    }
  }, [tenantId, campaignId]);

  useEffect(() => { load(); }, [load]);

  const changeStatus = async (s) => {
    try {
      await setCampaignStatus(tenantId, campaignId, s);
      load();
    } catch (err) {
      setError(err?.response?.data?.message || err.message || 'Could not change status');
    }
  };

  const footer = (
    <AppFooter
      buttons={[
        { icon: <FaArrowLeft />, label: 'Back', onClick: () => navigate(`/${tenantId}/campaigns`) },
        { icon: <FaHome />, label: 'Home', onClick: () => navigate(`/${tenantId}/dashboard`) },
      ]}
    />
  );

  if (loading) return <div className="p-6 text-sm opacity-70" style={{ paddingBottom: 72 }}>Loading…{footer}</div>;
  if (error && !campaign) return <div className="p-6 text-sm text-red-600" style={{ paddingBottom: 72 }}>{error}{footer}</div>;
  if (!campaign) return null;

  const kpis = roi?.kpis || {};
  const kpiKeys = Object.keys(kpis);

  return (
    <div className="mx-auto max-w-3xl space-y-5 p-4" style={{ paddingBottom: 72 }}>
      <div className="flex items-start justify-between gap-2">
        <div>
          <h1 className="text-2xl font-semibold">{campaign.name}</h1>
          <p className="text-sm capitalize opacity-60">
            {campaign.objective}
            {campaign.eventName ? ` · ${campaign.eventName}` : ''}
          </p>
        </div>
        {canEdit && (
          <button onClick={() => setEditing(true)} className="rounded border border-black/15 px-3 py-1.5 text-sm dark:border-white/15">Edit</button>
        )}
      </div>

      {error && <div className="text-sm text-red-600">{error}</div>}

      <div className="flex flex-wrap items-center gap-2">
        <span className="text-xs font-semibold uppercase tracking-wide opacity-60">Status</span>
        {canEdit ? (
          <select value={campaign.status} onChange={(e) => changeStatus(e.target.value)}
            className="rounded border border-black/15 bg-transparent px-2 py-1 text-sm capitalize dark:border-white/15">
            {CAMPAIGN_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        ) : (
          <span className="capitalize">{campaign.status}</span>
        )}
        <button
          onClick={() => navigate(`/${tenantId}/campaigns/${campaignId}/calendar`)}
          className="ml-auto inline-flex items-center gap-1 rounded border border-black/15 px-3 py-1.5 text-sm dark:border-white/15"
        >
          <FaRegCalendarAlt size={12} /> Content calendar
        </button>
        {campaign.eventid && canEdit && (
          <button onClick={() => setPlanning(true)} className="rounded bg-cobalt px-3 py-1.5 text-sm text-white">
            Generate plan
          </button>
        )}
      </div>

      {/* KPI goal vs actual */}
      <section className="rounded-lg border border-black/10 p-4 dark:border-white/10">
        <h3 className="mb-2 text-sm font-semibold">Goals</h3>
        {kpiKeys.length === 0 ? (
          <p className="text-sm opacity-60">No KPI goals set. Edit the campaign to add targets.</p>
        ) : (
          <div className="space-y-2">
            {kpiKeys.map((k) => {
              const row = kpis[k];
              const pct = row.pct == null ? null : Math.min(100, Math.max(0, row.pct));
              return (
                <div key={k}>
                  <div className="flex justify-between text-xs">
                    <span className="capitalize">{k}</span>
                    <span className="opacity-70">
                      {row.actual == null ? '—' : row.actual} / {row.target}
                      {row.pct != null ? ` · ${row.pct}%` : ''}
                    </span>
                  </div>
                  <div className="mt-0.5 h-1.5 overflow-hidden rounded-full bg-black/10 dark:bg-white/10">
                    <div className="h-full rounded-full bg-emerald-500" style={{ width: `${pct ?? 0}%` }} />
                  </div>
                </div>
              );
            })}
          </div>
        )}
        {roi?.engagement && (
          <div className="mt-3 grid grid-cols-3 gap-2 border-t border-black/10 pt-3 text-center text-xs dark:border-white/10">
            <div><div className="text-lg font-semibold">{roi.engagement.postsPublished}</div><div className="opacity-60">posts published</div></div>
            <div><div className="text-lg font-semibold">{roi.engagement.impressions.toLocaleString()}</div><div className="opacity-60">impressions</div></div>
            <div><div className="text-lg font-semibold">{roi.engagement.engagements.toLocaleString()}</div><div className="opacity-60">engagements</div></div>
          </div>
        )}
        {roi?.email && roi.email.sends > 0 && (
          <div className="mt-2 grid grid-cols-3 gap-2 border-t border-black/10 pt-3 text-center text-xs dark:border-white/10">
            <div><div className="text-lg font-semibold">{roi.email.delivered.toLocaleString()}</div><div className="opacity-60">emails sent</div></div>
            <div><div className="text-lg font-semibold">{roi.email.opened.toLocaleString()}</div><div className="opacity-60">opened</div></div>
            <div><div className="text-lg font-semibold">{roi.email.clicked.toLocaleString()}</div><div className="opacity-60">clicked</div></div>
          </div>
        )}
      </section>

      <AttributionPanel tenantId={tenantId} campaignId={campaignId} />

      <CampaignEmailPanel tenantId={tenantId} campaignId={campaignId} eventId={campaign.eventid} />

      {/* Content pipeline */}
      <section className="rounded-lg border border-black/10 p-4 dark:border-white/10">
        <h3 className="mb-2 text-sm font-semibold">Content pipeline ({content.total})</h3>
        {content.total === 0 ? (
          <p className="text-sm opacity-60">No content yet. Generate a plan or schedule posts linked to this campaign.</p>
        ) : (
          <div className="space-y-3">
            {STAGE_ORDER.filter((s) => (content.byStage[s] || []).length).map((s) => (
              <div key={s}>
                <div className="mb-1 text-xs font-semibold uppercase tracking-wide opacity-60">
                  {APPROVAL_LABELS[s]} ({content.byStage[s].length})
                </div>
                <ul className="space-y-1 text-sm">
                  {content.byStage[s].map((p) => (
                    <li key={p.id} className="flex justify-between gap-2 rounded bg-black/5 px-2 py-1 dark:bg-white/5">
                      <span className="truncate">{p.content || '(media)'}</span>
                      <span className="whitespace-nowrap opacity-50">
                        {p.scheduled_on ? String(p.scheduled_on).slice(0, 10) : '—'}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        )}
      </section>

      {editing && (
        <CampaignForm
          tenantId={tenantId}
          campaign={campaign}
          onClose={() => setEditing(false)}
          onSaved={() => { setEditing(false); load(); }}
        />
      )}
      {planning && (
        <GeneratePlanModal
          tenantId={tenantId}
          eventId={campaign.eventid}
          eventName={campaign.eventName}
          onClose={() => setPlanning(false)}
          onDone={() => load()}
        />
      )}

      {footer}
    </div>
  );
}
